import React, { useEffect, useState } from "react";
import { Table, Spinner, Button, Input } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

const BureauReporting = () => {
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [fintechList, setFintechList] = useState([]);
  const [selectedFintechId, setSelectedFintechId] = useState("");
  const [downloadingRow, setDownloadingRow] = useState(null);

  // =================== Fetch Fintech/LSP List for Dropdown ===================
  useEffect(() => {
    const fetchFintechs = async () => {
      try {
        const res = await axiosInstance.get(URL.LSP_LIST);
        const active = (res.data || []).filter((f) => f.isActive);
        setFintechList(active);
      } catch (err) {
        console.error("Failed to fetch fintech list:", err);
      }
    };
    fetchFintechs();
  }, []);

  // =================== Fetch Bureau Report Files ===================
  const fetchBureauReports = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(URL.BUREAU_REPORT_ALL);
      const data = Array.isArray(response.data) ? response.data : [];
      const sorted = [...data].sort(
        (a, b) => new Date(b.CreatedDate) - new Date(a.CreatedDate)
      );
      setFilteredRows(sorted);
    } catch (error) {
      console.error("Error fetching bureau reports:", error);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBureauReports();
  }, []);

  // =================== Search (Generate Excel + S3 Entry) ===================
  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both From Date and To Date");
      return;
    }

    setGenerating(true);
    try {
      const params = { fromDate, toDate };
      if (selectedFintechId) params.fintechId = selectedFintechId;

      await axiosInstance.get(URL.BUREAU_REPORT_EXPORT, { params });

      await fetchBureauReports();
    } catch (error) {
      console.error("Error generating bureau report:", error);
      alert("Failed to generate bureau report.");
    } finally {
      setGenerating(false);
    }
  };

  // =================== Export Excel (Download) ===================
  const exportToExcel = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From Date and To Date before exporting");
      return;
    }

    setExporting(true);
    try {
      const params = { fromDate, toDate };
      if (selectedFintechId) params.fintechId = selectedFintechId;

      const response = await axiosInstance.get(URL.BUREAU_REPORT_EXPORT, {
        params,
        responseType: "blob",
      });

      const selectedFintech = fintechList.find(
        (f) => String(f.id) === String(selectedFintechId)
      );
      const suffix = selectedFintech
        ? `_${selectedFintech.brandName || selectedFintech.name}`
        : "_All";
      const fileName = `Bureau_Report_${fromDate}_to_${toDate}${suffix}.xlsx`;

      triggerDownload(response.data, fileName);
    } catch (error) {
      console.error("Error exporting bureau report:", error);
      alert("Failed to export Excel.");
    } finally {
      setExporting(false);
    }
  };

  // =================== Export All Fintechs (one file per fintech + one combined) ===================
  const exportAllFintechs = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From Date and To Date before exporting");
      return;
    }
    if (fintechList.length === 0) {
      alert("No active fintechs found.");
      return;
    }

    setExportingAll(true);
    try {
      // Combined report (no fintechId filter)
      const combined = await axiosInstance.get(URL.BUREAU_REPORT_EXPORT, {
        params: { fromDate, toDate },
        responseType: "blob",
      });
      triggerDownload(
        combined.data,
        `Bureau_Report_${fromDate}_to_${toDate}_All.xlsx`
      );

      // Per-fintech reports
      for (const fintech of fintechList) {
        const res = await axiosInstance.get(URL.BUREAU_REPORT_EXPORT, {
          params: { fromDate, toDate, fintechId: fintech.id },
          responseType: "blob",
        });
        const suffix = fintech.brandName || fintech.name;
        triggerDownload(
          res.data,
          `Bureau_Report_${fromDate}_to_${toDate}_${suffix}.xlsx`
        );
      }

      await fetchBureauReports();
    } catch (error) {
      console.error("Error exporting all fintech reports:", error);
      alert("Failed to export one or more fintech reports.");
    } finally {
      setExportingAll(false);
    }
  };

  // =================== Parse params from filename ===================
  // Filename format: SALOCAPLTD_NB34030001_Consumerdata_{ddMMyyyy}_{ddMMyyyy}_{HHmmss}[_{brandName}].xlsx
  const parseParamsFromFileName = (fileName) => {
    try {
      const withoutExt = fileName.replace(".xlsx", "");
      const parts = withoutExt.split("_");
      // parts[3]=fromDate, parts[4]=reportedDate (= toDate-1), parts[5]=timestamp, parts[6..]=brandName

      const toYMD = (ddmmyyyy) => {
        const d = ddmmyyyy.substring(0, 2);
        const m = ddmmyyyy.substring(2, 4);
        const y = ddmmyyyy.substring(4, 8);
        return `${y}-${m}-${d}`;
      };

      const fromDate = toYMD(parts[3]);

      const toDate = toYMD(parts[4]);

      // Match brand name suffix to fintechId
      let fintechId = null;
      if (parts.length > 6) {
        const brandName = parts.slice(6).join("_");
        const matched = fintechList.find(
          (f) => (f.brandName || f.name) === brandName
        );
        if (matched) fintechId = matched.id;
      }

      return { fromDate, toDate, fintechId };
    } catch {
      return null;
    }
  };

  // =================== Download row via export API ===================
  const handleRowDownload = async (row, index) => {
    setDownloadingRow(index);
    try {
      const parsed = parseParamsFromFileName(row.FileName);
      if (!parsed) {
        alert("Could not parse report parameters from filename.");
        return;
      }

      const params = { fromDate: parsed.fromDate, toDate: parsed.toDate };
      if (parsed.fintechId) params.fintechId = parsed.fintechId;

      const response = await axiosInstance.get(URL.BUREAU_REPORT_EXPORT, {
        params,
        responseType: "blob",
      });

      triggerDownload(response.data, row.FileName);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    } finally {
      setDownloadingRow(null);
    }
  };

  const triggerDownload = (blobData, fileName) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // =================== Styles ===================
  const font12 = { fontSize: "11px" };
  const buttonSmStyle = {
    ...font12,
    padding: "4px 10px",
    height: "30px",
    lineHeight: "1",
  };

  return (
    <div style={font12}>
      {/* =================== Filter Section =================== */}
      <div className="d-flex align-items-center mb-3 flex-wrap" style={{ gap: 10 }}>
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{ ...buttonSmStyle, width: 140 }}
        />

        <Input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{ ...buttonSmStyle, width: 140 }}
        />

        {/* Fintech Brand Name Dropdown */}
        <Input
          type="select"
          value={selectedFintechId}
          onChange={(e) => setSelectedFintechId(e.target.value)}
          style={{ ...buttonSmStyle, width: 180 }}
        >
          <option value="">All Fintechs</option>
          {fintechList.map((f) => (
            <option key={f.id} value={f.id}>
              {f.brandName || f.name}
            </option>
          ))}
        </Input>

        <Button
          color="primary"
          onClick={handleSearch}
          style={buttonSmStyle}
          disabled={generating}
        >
          {generating ? "Generating..." : "Search"}
        </Button>

        <Button
          color="success"
          onClick={exportToExcel}
          style={buttonSmStyle}
          disabled={exporting}
        >
          {exporting ? "Exporting..." : "Export Excel"}
        </Button>

        <Button
          color="warning"
          onClick={exportAllFintechs}
          style={buttonSmStyle}
          disabled={exportingAll}
          title="Downloads one combined report + one per active fintech"
        >
          {exportingAll ? "Exporting All..." : "Export All Fintechs"}
        </Button>
      </div>

      {/* =================== Table =================== */}
      {loading ? (
        <div className="d-flex align-items-center mt-3" style={{ gap: 8 }}>
          <Spinner size="sm" />
          <span>Loading Bureau Reports...</span>
        </div>
      ) : filteredRows.length === 0 ? (
        <p className="mt-3 text-danger">No bureau reports found!</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table bordered striped hover responsive size="sm">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>File Name</th>
                <th>Created Date</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, index) => (
                <tr key={index}>
                  <td>{filteredRows.length - index}</td>
                  <td>{row.FileName}</td>
                  <td>
                    {row.CreatedDate
                      ? new Date(row.CreatedDate).toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    <Button
                      color="info"
                      size="sm"
                      disabled={downloadingRow === index}
                      onClick={() => handleRowDownload(row, index)}
                    >
                      {downloadingRow === index ? "Downloading..." : "Download"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default BureauReporting;
